import _ from 'lodash';

import {
  date as commonDate,
  typeguards as commonTypeguards,
  utils as commonUtils,
  finleyAssert,
} from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import {
  CoreBorrowingBaseInsightsGqlDataGQLType,
  CreditFacilityGQLType,
  FinancialCovenantsCalculationGQLType,
  LedgerTransactionWithCreditFacilityIdGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { parseInterestRateDisplayForCapitalProvider } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/utils';

export const portfolioOverviewFacilityKeys = [
  'creditFacilityName',
  'commitmentAmount',
  'loanOutstanding',
  'utilization',
  'dateOfLastFundingRequest',
  'borrowingBaseCashContribution',
  'totalBorrowingBase',
  'interestRate',
  'accruedInterestAndFees',
  'financialCovenantsFailed',
  'portfolioCovenantsFailed',
] as const;

export type PortfolioOverviewFacilityRow = {
  creditFacilityName: string;
  commitmentAmount: number;
  loanOutstanding: number;
  utilization: number;
  dateOfLastFundingRequest?: Util.FullDateStr;
  borrowingBaseCashContribution: number;
  totalBorrowingBase: number;
  interestRate: string;
  accruedInterestAndFees: number;
  financialCovenantsFailed: number | null; // 0 failed means pass, null means no calculations exist
  portfolioCovenantsFailed: number | null; // 0 failed means pass, null means no calculations exist
};

export type DrawRequestsMetadata = {
  id: string;
  creditFacilityId: string;
  drawRequestTotalAmount: number;
  created_at: number;
};

// CONSTS //

export const stickyColumnHeaderId: typeof portfolioOverviewFacilityKeys[number] =
  'creditFacilityName';
export const checkmarkColumnHeaderIds: typeof portfolioOverviewFacilityKeys[number][] = [
  'portfolioCovenantsFailed',
  'financialCovenantsFailed',
];
export const stickyHeaderColumnWidth = '400px';
export const stickyHeaderRowZIndex = 1; // top row of table, frozen for vertical scrolling
export const stickyHeaderColumnZIndex = 2; // left-most column of table, frozen for horizontal scrolling

export const fixedColumnsStyleToColumnIndexMap = [
  // facility column, sticky
  {
    width: stickyHeaderColumnWidth,
    minWidth: stickyHeaderColumnWidth,
    maxWidth: stickyHeaderColumnWidth,
    left: 0,
    zIndex: stickyHeaderColumnZIndex,
    borderRightWidth: 'medium',
  },
];

// DATA FORMATTERS //

export const groupFundingRequestsByCreditFacility = (
  fundingRequests: DrawRequestsMetadata[],
) => {
  return _.groupBy(fundingRequests, ({ creditFacilityId }) => creditFacilityId);
};

export const getDateOfLastFundingRequestForCreditFacility = (
  fundingRequestsForCreditFacility: DrawRequestsMetadata[],
) => {
  if (!fundingRequestsForCreditFacility?.length) {
    return undefined;
  }
  const lastFundingRequest = _.orderBy(
    fundingRequestsForCreditFacility,
    'created_at',
    'desc',
  )[0];

  return lastFundingRequest
    ? commonDate.epochMsToFullDateStr(lastFundingRequest.created_at)
    : undefined;
};

const groupByCreditFacilityId = <T extends { creditFacilityId: string }>(
  itemsWithCreditFacilityId: T[],
) => {
  return _.groupBy(
    itemsWithCreditFacilityId,
    ({ creditFacilityId }) => creditFacilityId,
  );
};

const doesCovenantCalculationPass = (
  covenantCalculation: FinancialCovenantsCalculationGQLType,
): boolean => {
  const { limitType, limitValue, value } = covenantCalculation;
  switch (limitType) {
    case 'max':
      if (value < limitValue) {
        return true;
      }
      break;
    case 'min':
      if (value > limitValue) {
        return true;
      }
      break;
    default:
      return false;
  }
  return false;
};

export const formatPortfolioOverviewFacilityRow = ({
  creditAgreements,
  fundingRequests,
  ledgerBalances,
  covenantCalculations,
  creditFacilities,
  organizationId,
}: {
  creditAgreements: CreditAgreement.DomainEntity.CreditAgreementFullArtifact[];
  fundingRequests: DrawRequestsMetadata[];
  ledgerBalances: LedgerTransactionWithCreditFacilityIdGQLType[];
  covenantCalculations: FinancialCovenantsCalculationGQLType[];
  creditFacilities: Pick<CreditFacilityGQLType, 'id' | 'slug'>[];
  organizationId: string;
}): PortfolioOverviewFacilityRow[] => {
  const groupedCreditAgreements = groupByCreditFacilityId(creditAgreements);
  const groupedFundingRequests = groupByCreditFacilityId(fundingRequests);
  const groupedLedgerBalances = groupByCreditFacilityId(ledgerBalances);
  const groupedCovenantCalculations = groupByCreditFacilityId(
    covenantCalculations,
  );

  return _.compact(
    _.map(
      creditFacilities,
      ({ id: creditFacilityId, slug: creditFacilitySlug }) => {
        const creditAgreementsForFacility =
          groupedCreditAgreements[creditFacilityId];
        const fundingRequestsForFacility =
          groupedFundingRequests[creditFacilityId];
        const ledgerBalanceForCreditFacility =
          groupedLedgerBalances[creditFacilityId];
        const covenantCalculationsForFacility =
          groupedCovenantCalculations[creditFacilityId];

        finleyAssert(
          creditAgreementsForFacility.length === 1,
          `there should only be one latest credit agreement for [creditFacilityId: ${creditFacilityId}. Got ${creditAgreementsForFacility.length} instead.`,
        );

        const { portfolioCovenantsFailed, financialCovenantsFailed } = _.reduce(
          covenantCalculationsForFacility,
          (acc, covenantCalc) => {
            if (covenantCalc.covenantType === 'financial') {
              // 2/12/24: previously all covenants were being inserted as financial covenants for demo purposes, so we need to first filter out the ones which were being demo'd as portfolio covenants
              // once portcovs are fully live on dashboard, we can clean up and deprecate the old demo portcov keys and remove this filter
              if (
                _.includes(
                  _.values(
                    CreditAgreement.Config.FinancialCovenant
                      .PortfolioCovenantKey,
                  ),
                  covenantCalc.covenantKey,
                )
              ) {
                return acc;
              }

              let updatedFinancialCovenantsFailed = _.isNumber(
                acc.financialCovenantsFailed,
              )
                ? acc.financialCovenantsFailed
                : 0;

              if (!doesCovenantCalculationPass(covenantCalc)) {
                updatedFinancialCovenantsFailed += 1;
              }

              return {
                ...acc,
                financialCovenantsFailed: updatedFinancialCovenantsFailed,
              };
            }

            // 2/12/24: currently all covenants are being inserted as financial covenants, so this will always show all no portfolio covenants calculations available since there are no portfolio covenantType records
            // once we start correctly inserting covenantType==='portfolio' records, thi will work as expected and we can remove this note
            if (covenantCalc.covenantType === 'portfolio') {
              let updatedPortfolioCovenantsFailed = _.isNumber(
                acc.portfolioCovenantsFailed,
              )
                ? acc.portfolioCovenantsFailed
                : 0;

              if (!doesCovenantCalculationPass(covenantCalc)) {
                updatedPortfolioCovenantsFailed += 1;
              }

              return {
                ...acc,
                portfolioCovenantsFailed: updatedPortfolioCovenantsFailed,
              };
            }

            return acc;
          },
          {
            portfolioCovenantsFailed: null,
            financialCovenantsFailed: null,
          } as {
            portfolioCovenantsFailed: number | null;
            financialCovenantsFailed: number | null;
          },
        );

        const {
          facilityMechanics: {
            parties: { capitalProviders },
          },
        } = creditAgreementsForFacility[0];

        const capitalProvider = _.find(
          capitalProviders,
          (cp) => cp.entity.id === organizationId,
        );

        if (!capitalProvider) {
          return undefined;
        }

        const portfolioOverviewFacilityRow: PortfolioOverviewFacilityRow = {
          creditFacilityName: creditFacilitySlug,
          commitmentAmount: capitalProvider.commitmentAmount.value,
          dateOfLastFundingRequest: getDateOfLastFundingRequestForCreditFacility(
            fundingRequestsForFacility,
          ),
          interestRate: parseInterestRateDisplayForCapitalProvider(
            capitalProvider,
          ),
          accruedInterestAndFees: ledgerBalanceForCreditFacility
            ? ledgerBalanceForCreditFacility[0].ledger.balance
            : 0,
          financialCovenantsFailed,
          portfolioCovenantsFailed,
          // the below will be populated by borrowing base insights from datalake at a per-facility level later
          loanOutstanding: 0,
          utilization: 0,
          borrowingBaseCashContribution: 0,
          totalBorrowingBase: 0,
        };

        return portfolioOverviewFacilityRow;
      },
    ),
  );
};

// REACT TABLE UTILS //

export const createInitialColumnVisibility = (selectedOptions?: string[]) => {
  const initialColumnVisibility: { [key: string]: boolean } = {};
  _.forEach(portfolioOverviewFacilityKeys, (key) => {
    initialColumnVisibility[key] = true;

    if (selectedOptions && !_.includes(selectedOptions, key)) {
      initialColumnVisibility[key] = false;
    }
  });

  return initialColumnVisibility;
};

const formatColumnDef = (
  accessorKey: typeof portfolioOverviewFacilityKeys[number],
  header?: string,
) => {
  return {
    accessorKey,
    header: header ?? commonUtils.toTitleCase(accessorKey),
  };
};

export const portfolioOverviewFacilitiesTableColumns = _.map(
  portfolioOverviewFacilityKeys,
  (key) => {
    switch (key) {
      case 'creditFacilityName':
        return formatColumnDef(key, 'Facility');
      case 'dateOfLastFundingRequest':
        return formatColumnDef(key, 'Date of Last Funding Request');
      case 'financialCovenantsFailed':
        return formatColumnDef(key, 'Financial Covenants');
      case 'portfolioCovenantsFailed':
        return formatColumnDef(key, 'Portfolio Covenants');
      default:
        return formatColumnDef(key);
    }
  },
);

export const formatCellValue = ({
  cellValue,
  columnId,
  defaultValue = '-',
}: {
  cellValue: unknown;
  columnId: typeof portfolioOverviewFacilityKeys[number];
  defaultValue?: string;
}): string => {
  if (_.isNil(cellValue)) {
    return defaultValue;
  }
  const cellValueString = `${cellValue}`;
  let formattedCellValue = cellValueString;
  switch (columnId) {
    case 'creditFacilityName':
      if (typeof cellValue === 'string') {
        formattedCellValue = commonUtils.creditFacilitySlugToName(cellValue);
      }
      break;
    case 'commitmentAmount':
    case 'loanOutstanding':
    case 'borrowingBaseCashContribution':
    case 'totalBorrowingBase':
    case 'accruedInterestAndFees':
      if (typeof cellValue === 'number') {
        formattedCellValue =
          commonUtils.numberToCurrencyWithSymbol({
            number: cellValue,
          }) || cellValueString;
      }
      break;

    case 'financialCovenantsFailed':
    case 'portfolioCovenantsFailed':
      formattedCellValue = cellValue === 0 ? 'Passed' : `${cellValue} Failed`;
      break;

    case 'utilization':
      if (typeof cellValue === 'number') {
        formattedCellValue = commonUtils.numberToFormattedString(
          cellValue,
          'percentage',
          false,
        );
      }
      break;

    case 'dateOfLastFundingRequest':
      if (commonTypeguards.isFullDateStr(cellValue)) {
        formattedCellValue = commonDate.fullDateStrToHumanDateString(cellValue);
      }
      break;

    default:
      formattedCellValue = cellValueString;
  }
  return formattedCellValue;
};

export const borrowingBaseInsightsColumnIds: typeof portfolioOverviewFacilityKeys[number][] = [
  'loanOutstanding',
  'borrowingBaseCashContribution',
  'totalBorrowingBase',
  'utilization',
];

export const getBorrowingBaseInsightsValueForColumn = ({
  columnId,
  insights,
}: {
  columnId: typeof borrowingBaseInsightsColumnIds[number];
  insights: CoreBorrowingBaseInsightsGqlDataGQLType;
}): Util.Maybe<number> => {
  const {
    currentPrincipalBalance,
    totalCashBorrowingBaseAmount,
    totalBorrowingBaseAmount,
    totalFacilityUtilization,
  } = insights;
  switch (columnId) {
    case 'loanOutstanding':
      return currentPrincipalBalance;
    case 'borrowingBaseCashContribution':
      return totalCashBorrowingBaseAmount;
    case 'totalBorrowingBase':
      return totalBorrowingBaseAmount;
    case 'utilization':
      return totalFacilityUtilization;
    default:
      return undefined;
  }
};
